<template>
  <NModal
    :bordered="false"
    class="cb-link-settings"
    :onEsc="() => setComponentEditMode(false)"
    preset="card"
    :show="true"
    size="huge"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="t('canvasUI.canvasBuilder.link.editLinkComponent')"
    @close="setComponentEditMode(false)"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="flex"
      embedded
      role="dialog"
      :style="{
        '--n-color-embedded-modal': themeVars.base,
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <div class="cb-link-settings__card grid grid-cols-1 sm:grid-cols-2 py-4 overflow-auto">
        <div class="cb-link-settings__form h-full">
          <NForm ref="formRef" class="flex flex-col w-full h-full" :model="formValues">
            <NFormItem
              :label="t('canvasUI.canvasBuilder.link.form.labels.linkType')"
              path="url"
              :rule="
                formValues.link_type === LinkAnchorTypes.WEB
                  ? {
                      level: 'warning',
                      trigger: ['blur'],
                      validator: urlValidator,
                    }
                  : undefined
              "
            >
              <div class="flex flex-col w-full gap-2">
                <CSelect
                  v-model:value="formValues.link_type"
                  bordered
                  class="select-none"
                  :on-update:value="updateLinkType"
                  :options="linkTypeOptions"
                />
                <NInput
                  v-if="formValues.link_type === LinkAnchorTypes.WEB"
                  v-model:value="formValues.url"
                  placeholder="https://example.com"
                />
                <CSelect
                  v-else-if="formValues.link_type === LinkAnchorTypes.CANVAS_ANCHOR"
                  v-model:value="formValues.canvas_anchor"
                  bordered
                  class="basis-1/2 select-none"
                  :options="anchoringOptions"
                />
                <CSingleFileSelector
                  v-else-if="formValues.link_type === LinkAnchorTypes.FILE"
                  :file="formValues.file ?? null"
                  :is-selecting="false"
                  :selected-page-index="formValues.page_index"
                  size="small"
                  @clearSelectedFile="clearFile"
                  @openContentSelector="chooseContent"
                />
                <NCheckbox
                  v-if="formValues.link_type === LinkAnchorTypes.WEB"
                  v-model:checked="formValues.target"
                  checked-value="_blank"
                  unchecked-value="_self"
                  >{{ t('canvasUI.canvasBuilder.link.form.labels.openLinkInNewTab') }}
                </NCheckbox>
              </div>
            </NFormItem>
            <NFormItem :label="t('canvasUI.canvasBuilder.link.form.labels.previewType')">
              <div class="flex gap-2 w-full">
                <CSelect
                  v-model:value="formValues.preview_type"
                  bordered
                  class="basis-1/2 select-none"
                  :on-update:value="updatePreviewType"
                  :options="previewTypeOptions"
                />
                <NColorPicker
                  v-if="formValues.preview_type === LinkPreviewTypes.SOLID_BUTTON"
                  v-model:value="formValues.background_color"
                  :actions="['clear', 'confirm']"
                  class="basis-1/2"
                  default-value=""
                  :modes="['hex']"
                  :swatches="showSwatches ? palette : undefined"
                  :updateInputModelOnDrop="true"
                />
              </div>
            </NFormItem>
            <NFormItem :label="t('canvasUI.canvasBuilder.link.form.labels.displayText')">
              <NInput v-model:value="formValues.display_text" placeholder="" />
            </NFormItem>
            <NFormItem :label="t('canvasUI.canvasBuilder.link.form.labels.text')">
              <div class="flex flex-col gap-2 w-full">
                <CFontSelect v-model="formValues.font_family" bordered class="select-none" />
                <div class="flex gap-2 w-full">
                  <NInputNumber
                    v-model:value="formValues.font_size"
                    button-placement="both"
                    class="basis-1/2 text-center"
                    :format="(value) => `${value}px`"
                    :min="0"
                    :parse="(formattedValue) => parseInt(formattedValue)"
                    placeholder="16"
                  />
                  <NColorPicker
                    v-model:value="formValues.color"
                    :actions="['clear', 'confirm']"
                    class="basis-1/2"
                    default-value=""
                    :modes="['hex']"
                    :swatches="showSwatches ? palette : undefined"
                    :updateInputModelOnDrop="true"
                  />
                </div>
              </div>
            </NFormItem>
            <NFormItem :label="t('canvasUI.canvasBuilder.link.form.labels.alignmentType')">
              <CSelect
                v-model:value="formValues.alignment"
                bordered
                class="select-none"
                :options="alignmentTypeOptions"
              />
            </NFormItem>
          </NForm>
        </div>
        <div class="cb-link-settings__preview rounded-lg">
          <div class="pa-4 h-full sm:mx-4" :style="{ backgroundColor: themeVars.primary6 }">
            <span class="font-bold text-sm">
              {{ t('canvasUI.canvasBuilder.link.preview') }}
            </span>
            <RawLink class="h-full" :data="formValues" v-bind="$attrs" />
          </div>
        </div>
      </div>
    </NCard>

    <template #action>
      <div class="flex justify-end gap-2">
        <CButton @click="setComponentEditMode(false)">
          {{ t('canvasUI.common.cancel') }}
        </CButton>
        <CButton type="primary" @click="onSave">{{ t('canvasUI.common.done') }}</CButton>
      </div>
    </template>
  </NModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, type ComputedRef } from 'vue'
import { defaults } from 'lodash-es'
import {
  NModal,
  NCard,
  NInput,
  NCheckbox,
  NColorPicker,
  NForm,
  NFormItem,
  NInputNumber,
  type FormInst,
  type FormItemRule,
  type SelectOption,
} from 'naive-ui'
import { useI18n } from 'vue-i18n'

import RawLink from './Link.raw.vue'
import { adjustThemeByPreviewType, getPreviewTypeOptions } from './Link.util'
import CButton from '@lib/components/CButton/CButton.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CSingleFileSelector from '@lib/components/CSingleFileSelector/CSingleFileSelector.vue'
import CFontSelect from '@lib/components/CFontSelect/CFontSelect.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useContentSelector from '@canvas-builder/composables/useContentSelector'
import { ComponentTypes, LinkAnchorTypes, LinkPreviewTypes, type LinkProps } from '@canvas-builder/types/canvas'
import { getComponentTitle, getNodeDisplayNameByComponentType } from '@canvas-builder/util/canvas.util'
import { enforceHttps, isValidUrl } from '@canvas-builder/util/url'
import { DEFAULT_FONT } from '@canvas-builder/constants'
import { FileContentTypeEnum } from '@openapi'
import type { Selection } from '@lib/types/selections'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = defineProps<{
  id: string
  data: LinkProps | null
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const themeVars = useThemeVars()
const { canvasContent, updateNodeDataById, setComponentEditMode } = useCanvas()
const { palette } = useCanvasTheme()
const { setContentSelectorMode } = useContentSelector()

const formRef = ref<FormInst | null>(null)
const formValues = ref<LinkProps>(
  defaults(
    { ...props.data },
    { background_color: themeVars.value.primary, color: themeVars.value.base, font_family: DEFAULT_FONT },
  ),
)

const excludeComponentsFromNavigation = [ComponentTypes.Link, ComponentTypes.Root, 'Section']

const navigatableComponents = computed(() =>
  canvasContent.value.filter((component) => !excludeComponentsFromNavigation.includes(component.type)),
)

const showSwatches = computed(() => !!launchDarkly.value.enable_canvas_tokens || !!launchDarkly.value.enable_themes)

const anchoringOptions = ref<SelectOption[]>(
  navigatableComponents.value.map((component) => ({
    label: getNodeDisplayNameByComponentType(component) || getComponentTitle(component.type, t),
    value: component.id,
  })),
)

const alignmentTypeOptions = ref<SelectOption[]>([
  {
    label: t('canvasUI.canvasBuilder.link.form.alignmentTypes.left'),
    value: 'left',
  },
  {
    label: t('canvasUI.canvasBuilder.link.form.alignmentTypes.center'),
    value: 'center',
  },
  {
    label: t('canvasUI.canvasBuilder.link.form.alignmentTypes.right'),
    value: 'right',
  },
])

const linkTypeOptions = ref<SelectOption[]>([
  {
    label: t('canvasUI.canvasBuilder.link.form.linkTypes.web'),
    value: LinkAnchorTypes.WEB,
  },
  {
    label: t('canvasUI.canvasBuilder.link.form.linkTypes.anchor'),
    value: LinkAnchorTypes.CANVAS_ANCHOR,
  },
  {
    label: t('canvasUI.canvasBuilder.link.form.linkTypes.file'),
    value: LinkAnchorTypes.FILE,
  },
])

const previewTypeOptions = getPreviewTypeOptions(t)

function checkIfCanvasAnchorExists() {
  return !!navigatableComponents.value.find((component) => component.id === formValues.value.canvas_anchor)
}

function getSelections(): Selection[] {
  if (!formValues.value.file) return []

  if (!formValues.value.page_index) {
    return [
      {
        fileId: formValues.value.file.id ?? '',
        type: 'file',
      },
    ]
  }

  return [
    {
      fileId: formValues.value.file.id ?? '',
      pageIndex: formValues.value.page_index,
      type: 'page',
    },
  ]
}

function clearFile() {
  formValues.value.file = null
}

function chooseContent() {
  setContentSelectorMode({
    id: props.id,
    componentType: ComponentTypes.Link,
    allowedSelectorTypes: ['content'],
    allowedContentTypes: [
      FileContentTypeEnum.PDF,
      FileContentTypeEnum.VIDEO,
      FileContentTypeEnum.WEB,
      FileContentTypeEnum.IMAGE,
    ],
    maxSelections: 1,
    selections: getSelections(),
  })
}

function onSave() {
  formRef.value?.validate((errors, { warnings }) => {
    if (warnings) {
      console.warn(warnings)
    } else {
      updateNodeDataById(props.id, {
        ...formValues.value,
        url: enforceHttps(formValues.value.url!),
      })
      setComponentEditMode(false)
    }
  })
}

function updateLinkType(value: LinkAnchorTypes) {
  formValues.value.link_type = value

  formValues.value.canvas_anchor = ''
  formValues.value.file = null
  formValues.value.url = ''
}

function updatePreviewType(value: LinkPreviewTypes) {
  const { backgroundColor, color } = adjustThemeByPreviewType(value, formValues.value)
  formValues.value.preview_type = value
  formValues.value.color = color
  formValues.value.background_color = backgroundColor
}

function urlValidator(_: FormItemRule, v: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!v) return reject(new Error(t('canvasUI.canvasBuilder.link.form.errors.requiredUrl')))
    if (v && !isValidUrl(v)) return reject(new Error(t('canvasUI.canvasBuilder.link.form.errors.invalidUrl')))
    return resolve()
  })
}

onMounted(() => {
  if (formValues.value.link_type === LinkAnchorTypes.CANVAS_ANCHOR && !checkIfCanvasAnchorExists()) {
    formValues.value.canvas_anchor = ''
  }
})
</script>

<style lang="scss">
.cb-link-settings {
  width: 780px;
  height: clamp(350px, calc(100vh - 120px), 720px);

  .cb-link-raw .c-button {
    max-width: 260px;
  }
}

.cb-link-settings__card {
  max-height: clamp(100px, calc(100vh - 260px), 960px);
}
</style>
